(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Lightbox = global.NewHomeSource.Lightbox || {};

  const Lightbox = global.NewHomeSource.Lightbox;

  if (!global.NewHomeSource.Carousel.Index ||
    !Lightbox.State ||
    !Lightbox.CloseButton ||
    !Lightbox.VideosHandler ||
    !Lightbox.ExternalMediaHandler ||
    !Lightbox.Caption ||
    !Lightbox.Pagination) {
    return;
  }

  class Engine {
    /**
     * Initializes a new instance of the Lightbox engine.
     */
    constructor() {
      this.carousel = new global.NewHomeSource.Carousel.Index();
      this.state = new Lightbox.State();
      this.eventsToObserve = {
        close: this.destroy.bind(this),
      };
    }

    /**
     * Sets the template for the lightbox engine.
     * @param {string} template - The HTML template to use.
     * @returns {this} The current instance for chaining.
     */
    setTemplate(template) {
      this.template = template;
      return this;
    }

    /**
     * Initializes and displays the lightbox component with the specified carousel options and media item.
     *
     * @param {Object} carouselOptions - Options to configure the carousel behavior.
     * @param {string} mediaId - The identifier of the media item to display initially.
     */
    create(carouselOptions, mediaId) {
      Engine.disableScroll();
      requestAnimationFrame(() => {
        document.body.appendChild(this.template.content.cloneNode(true));
        const rootNode = document.querySelector('[data-component="lightbox"]');
        const carousel = rootNode.querySelector('[data-component="lightbox-carousel"]');
        const index = Engine.getIndexByMediaId(carousel, mediaId);
        const options = Object.assign({}, carouselOptions || {}, {
          index,
        });
        const lightboxDataElement = rootNode.querySelector('[data-component="lightbox-data"]');
        this.carousel.setOptions(options);
        this.carousel.create(carousel);
        this.state.setOptions({
          rootNode,
          lightboxDataElement
        });
        this.closeButton = new Lightbox.CloseButton();
        this.videosHandler = new Lightbox.VideosHandler();
        this.externalMediaHandler = new Lightbox.ExternalMediaHandler();
        this.caption = new Lightbox.Caption();
        this.pagination = new Lightbox.Pagination();
        this.zoom = Lightbox.Zoom ? new Lightbox.Zoom() : null;
        this.closeButton.initialize(this.state);
        this.videosHandler.initialize(this.state);
        this.externalMediaHandler.initialize(this.state);
        this.caption.initialize(this.state);
        this.pagination.initialize(this.state);
        if (this.zoom) {
          this.zoom.initialize(this.state);
        }
        this.addObservers();
        Engine.attachEvents(rootNode);
        carousel.focus();
        carousel.addEventListener('carouselChange', this.carouselChange.bind(this));
        this.dispatchCustomEvent('lightboxOpen', { bubbles: true });
      });
    }

    /**
     * Handles the carousel change event by notifying observers about the items in view and the current index.
     *
     * @param {CustomEvent} event - The event object containing carousel change details.
     * @param {Object} event.detail - The detail object from the event.
     * @param {number[]} event.detail.indexesElementsInView - Array of indexes for elements currently in view.
     * @param {number} event.detail.currentIndex - The index of the currently active carousel item.
     */
    carouselChange(event) {
      const data = event.detail;
      if (!data.indexesElementsInView.includes(data.currentIndex)) {
        return;
      }
      this.state.notifyObservers('itemsChange', {
        indexes: data.indexesElementsInView,
        currentIndex: data.currentIndex,
      });

      const rootNode = this.state.get('rootNode');
      const currentElement = rootNode.querySelector(`[data-column="${data.currentIndex}"]`);
      if (!currentElement) {
        return;
      }
      document.body.dispatchEvent(new CustomEvent('lightboxElementChange', {
        detail: {
          index: data.currentIndex,
          id: currentElement.dataset.mediaId,
          type: currentElement.dataset.type,
          url: currentElement.dataset.url,
          isVideo: currentElement.dataset.videoId !== undefined,
        }
      }));
    }

    /**
     * Cleans up and destroys the lightbox instance.
     */
    destroy() {
      this.dispatchCustomEvent('lightboxClose', { bubbles: true });
      const rootNode = this.state.get('rootNode');
      this.carousel.destroy();
      this.closeButton.destroy();
      this.videosHandler.destroy();
      this.externalMediaHandler.destroy();
      this.caption.destroy();
      this.pagination.destroy();
      if (this.zoom) {
        this.zoom.destroy();
      }
      Engine.enableScroll();
      rootNode.remove();
      this.removeObservers();
    }

    /**
     * Registers this instance as an observer for each event specified in `eventsToObserve`.
     * Iterates over all event keys and adds this instance as an observer to the state for each event.
     */
    addObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
    }

    /**
     * Removes this instance as an observer from all events listed in `eventsToObserve`.
     * Iterates over each event and calls `removeObserver` on the state object.
     */
    removeObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }

    /**
     * Updates the carousel by invoking the callback associated with the given topic.
     *
     * @param {*} data - The data to pass to the callback function.
     * @param {string} topic - The topic whose callback should be invoked.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }

    /**
     * Dispatches a custom event with the specified name and data from the root node.
     *
     * @param {string} eventName - The name of the custom event to dispatch.
     * @param {*} options - The options to include in the event's detail property.
     */
    dispatchCustomEvent(eventName, options) {
      const rootNode = this.state.get('rootNode');
      rootNode.dispatchEvent(new CustomEvent(eventName, options));
    }

    /**
     * Disables scrolling on the page by adding a CSS class to the body element.
     * The class 'nhs-u-overflow--hidden' should set overflow to hidden.
     */
    static disableScroll() {
      document.body.classList.add('nhs-u-overflow--hidden');
    }

    /**
     * Enables scrolling on the page by removing the 'nhs-u-overflow--hidden' class from the document body.
     * This is typically used to restore scroll functionality after it has been disabled (e.g., by a modal or lightbox).
     */
    static enableScroll() {
      document.body.classList.remove('nhs-u-overflow--hidden');
    }

    /**
     * Returns the index of a carousel item by its media ID.
     *
     * @param {HTMLElement} carousel - The carousel container element.
     * @param {string} mediaId - The media ID to search for.
     * @returns {number} The index of the carousel item with the given media ID, or 0 if not found.
     */
    static getIndexByMediaId(carousel, mediaId) {
      const element = carousel.querySelector(`[data-media-id="${mediaId}"]`);
      const index = (element && element.dataset.column) || 0;
      return parseInt(index, 10);
    }

    /**
    * Attaches click event listeners to call-to-action buttons within the lightbox.
    *
    * @param {HTMLElement} rootNode - The lightbox container element.
    */
    static attachEvents(rootNode) {
      if (!rootNode) {
        return;
      }
      const body = document.body;
      const btnTour = rootNode.querySelector('[data-cta-tour]');
      const btnContact = rootNode.querySelector('[data-cta-contact]');
      const btnBrochure = rootNode.querySelector('[data-cta-free-brochure]');

      if (btnTour) {
        btnTour.addEventListener('click', (event) => body.dispatchEvent(new CustomEvent('lightbox-tour-click', {
          detail: { originalEvent: event }
        })));
      }
      if (btnContact) {
        btnContact.addEventListener('click', (event) => body.dispatchEvent(new CustomEvent('lightbox-contact-click', {
          detail: { originalEvent: event }
        })));
      }
      if (btnBrochure) {
        btnBrochure.addEventListener('click', (event) => body.dispatchEvent(new CustomEvent('lightbox-brochure-click', {
          detail: { originalEvent: event }
        })));
      }
    }
  }

  global.NewHomeSource.Lightbox.Engine = Engine;
}(window));
